import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, Typography, Box, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TablePagination, MenuItem, Select,
    FormControl, InputLabel, AppBar, Toolbar, IconButton, makeStyles, Chip
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LoaderWithBackDrop from '../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import { getInsiderCreditLedger } from '../../../../services/billing.service';
import { CREDIT_TRANSACTION_TYPE, CREDIT_TRANSACTION_TYPE_VERBOSE } from '../../../../utils/constants';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        backgroundColor: '#f8f9fa',
        padding: theme.spacing(3),
    },
    table: {
        minWidth: 650,
    },
    filterContainer: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    balanceBox: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(1, 3),
        borderRadius: theme.spacing(1),
        display: 'inline-block',
        fontWeight: 'bold',
    },
    positiveAmount: {
        color: 'green',
        fontWeight: 'bold',
    },
    negativeAmount: {
        color: 'red',
        fontWeight: 'bold',
    }
}));

const EVENT_SOURCE_LABELS = {
    topup_purchase: 'Top-up Purchase',
    initial_grant: 'Initial Grant',
    trial_grant: 'Trial Grant',
    monthly_grant: 'Monthly Grant',
    program_creation: 'Program Creation',
    assessment_creation: 'Assessment Creation',
    user_program_access: 'User Program Access',
    user_assessment_access: 'User Assessment Access',
    admin_adjustment: 'Admin Adjustment'
};

const DQInsiderLedgerDialog = ({ open, onClose, dq_insider_id }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [ledgerData, setLedgerData] = useState({
        entries: [],
        total: 0,
        balance_credits: 0
    });
    const [filters, setFilters] = useState({
        page: 1,
        limit: 20,
        transaction_type: ''
    });

    useEffect(() => {
        if (open && dq_insider_id) {
            fetchLedger();
        } else {
            // Reset state when closed
            setLedgerData({ entries: [], total: 0, balance_credits: 0 });
            setFilters({ page: 1, limit: 20, transaction_type: '' });
        }
    }, [open, dq_insider_id, filters.page, filters.limit, filters.transaction_type]);

    const fetchLedger = async () => {
        setLoading(true);
        console.log('--- SENDING REQUEST TO API ---');
        console.log('Insider ID:', dq_insider_id);
        
        try {
            const { page, limit, transaction_type } = filters;
            const result = await getInsiderCreditLedger(dq_insider_id, page, limit, transaction_type);
            
            if (result && result.data) {
                console.log('Clicked Insider Ledger Data:', result.data);
                setLedgerData({
                    entries: result.data.entries || [],
                    total: result.data.total || 0,
                    balance_credits: result.data.credit_balance || 0
                });
            }
        } catch (error) {
            console.error('Error fetching ledger:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1
        }));
    };

    const getTransactionTypeColor = (type) => {
        switch (type) {
            case 'topup': return 'primary';
            case 'deduction': return 'secondary';
            case 'refund': return 'default';
            case 'adjustment': return 'default';
            default: return 'default';
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
            <AppBar position="static" color="white" elevation={0} style={{ borderBottom: '1px solid #eee' }}>
                <Toolbar style={{ justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="primary">PBDQ Credit Ledger</Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <DialogContent className={classes.dialogContent}>
                <Box className={classes.filterContainer}>
                    <Box className={classes.balanceBox}>
                        <Typography variant="subtitle1">
                            Current Balance: {ledgerData.balance_credits} Credits
                        </Typography>
                    </Box>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                        <InputLabel>Transaction Type</InputLabel>
                        <Select
                            value={filters.transaction_type}
                            onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                            label="Transaction Type"
                        >
                            <MenuItem value=""><em>All</em></MenuItem>
                            <MenuItem value={CREDIT_TRANSACTION_TYPE.TOPUP}>{CREDIT_TRANSACTION_TYPE_VERBOSE[CREDIT_TRANSACTION_TYPE.TOPUP]}</MenuItem>
                            <MenuItem value={CREDIT_TRANSACTION_TYPE.DEDUCTION}>{CREDIT_TRANSACTION_TYPE_VERBOSE[CREDIT_TRANSACTION_TYPE.DEDUCTION]}</MenuItem>
                            <MenuItem value={CREDIT_TRANSACTION_TYPE.REFUND}>{CREDIT_TRANSACTION_TYPE_VERBOSE[CREDIT_TRANSACTION_TYPE.REFUND]}</MenuItem>
                            <MenuItem value={CREDIT_TRANSACTION_TYPE.ADJUSTMENT}>{CREDIT_TRANSACTION_TYPE_VERBOSE[CREDIT_TRANSACTION_TYPE.ADJUSTMENT]}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <TableContainer component={Paper} elevation={1}>
                    <Table className={classes.table}>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Date</b></TableCell>
                                <TableCell><b>Transaction Type</b></TableCell>
                                <TableCell><b>Event Source</b></TableCell>
                                <TableCell><b>Description</b></TableCell>
                                <TableCell align="right"><b>Amount</b></TableCell>
                                <TableCell align="right"><b>Balance After</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ledgerData.entries.map((entry) => (
                                <TableRow key={entry._id}>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(entry.createdAt).toLocaleString('en-GB', {
                                            day: '2-digit', month: 'short', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: true
                                        })}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={entry.transaction_type} 
                                            size="small" 
                                            color={getTransactionTypeColor(entry.transaction_type)}
                                            style={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {EVENT_SOURCE_LABELS[entry.event_source] || entry.event_source || 'N/A'}
                                    </TableCell>
                                    <TableCell style={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {entry.description || 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography className={entry.amount_credits >= 0 ? classes.positiveAmount : classes.negativeAmount}>
                                            {entry.amount_credits > 0 ? '+' : ''}{entry.amount_credits}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography style={{ fontWeight: 500 }}>
                                            {entry.balance_after}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {ledgerData.entries.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No ledger entries found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={ledgerData.total}
                    rowsPerPage={filters.limit}
                    page={filters.page - 1}
                    onPageChange={(e, newPage) => handleFilterChange('page', newPage + 1)}
                    onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                />

                <LoaderWithBackDrop loading={loading} />
            </DialogContent>

            <DialogActions style={{ padding: '12px 24px' }}>
                <Button onClick={onClose} color="primary" variant="contained" disableElevation>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DQInsiderLedgerDialog;
